import React from "react";
import {
  Box,
  AppBar,
  Toolbar,
  Container,
  useTheme,
  alpha,
} from "@mui/material";
import { useRouter } from "next/router";
import Header from "./Header";
import Footer from "./Footer";
import { SelectThemeType } from "../../types/types";
import { motion, AnimatePresence } from "framer-motion";

interface PrimaryLayoutProps {
  children: React.ReactElement;
  toggleTheme?: React.MouseEventHandler<HTMLButtonElement>;
  selectedTheme: SelectThemeType;
}

export default function PrimaryLayout({
  children,
  toggleTheme,
  selectedTheme,
}: PrimaryLayoutProps) {
  const { asPath } = useRouter();
  const theme = useTheme();

  const isDark = selectedTheme.type === "dark";
  const headerBg = alpha(
    isDark ? theme.palette.background.default : theme.palette.background.paper,
    0.92
  );

  return (
    <AnimatePresence mode="wait">
      <Box
        key={asPath}
        component={motion.div}
        initial="initialState"
        animate="animateState"
        exit="exitState"
        transition={{ duration: 0.5 }}
        variants={{
          initialState: {
            opacity: 0,
            clipPath: "polygon(50% 0, 50% 0, 50% 100%, 50% 100%)",
          },
          animateState: {
            opacity: 1,
            clipPath: "polygon(0 0, 100% 0, 100% 100%, 0 100%)",
          },
          exitState: {
            clipPath: "polygon(50% 0, 50% 0, 50% 100%, 50% 100%)",
          },
        }}
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          background: isDark
            ? theme.palette.background.default
            : theme.palette.grey[50],
        }}
      >
        <AppBar
          position="fixed"
          elevation={0}
          sx={{
            top: 0,
            left: 0,
            right: 0,
            zIndex: (theme) => theme.zIndex.drawer + 2,
            backdropFilter: "blur(14px)",
            backgroundColor: headerBg,
            borderBottom: `1px solid ${alpha(theme.palette.divider, 0.4)}`,
          }}
        >
          <Toolbar disableGutters sx={{ minHeight: { xs: 72, md: 80 } }}>
            <Container maxWidth="lg">
              <Header toggleTheme={toggleTheme} selectedTheme={selectedTheme} />
            </Container>
          </Toolbar>
        </AppBar>
        <Box
          component="main"
          flexGrow={1}
          sx={{ pt: { xs: "96px", md: "112px" } }}
        >
          {children}
        </Box>
        <Footer />
      </Box>
    </AnimatePresence>
  );
}
